/**
 * useCredentialsSettingsEditor Hook
 *
 * Manages editing of credential reader settings (NFC/Mifare toggles):
 * - Modal open/close state
 * - Which setting is being edited and its current value
 * - Save/cancel operations with pessimistic UI updates
 * - Full device sync on success
 */

import { useState, useCallback, useRef } from "react";
import logger from "../utils/logger";

const CREDENTIAL_SETTING_LABELS = {
  appleNFC: "Apple NFC",
  androidHce: "Android NFC",
  mifare2Go: "Mifare2Go",
  mifareDesfireLight: "Mifare Desfire Light",
  mifareDesfire: "Mifare Desfire",
  mifareClassic: "Mifare Classic",
  nfcConfigCard: "NFC Config Cards",
  receiveWifiConnectionEvents: "Wi-Fi Connection Events",
  keyOverrideEventEnabled: "Key Override Events",
  unauthorizedUserEventEnabled: "Unauthorized User Events",
  rexEventEnabled: "Request to Exit Events",
  interiorLockBlinkingEnabled: "Interior Lock Blinking",
  doubleTapEnabled: "Double Tap Feature",
  bruteForceMitigationSetting: "Brute Force Mitigation",
  powerFailLockMode: "Power Fail Lock Mode",
  blockConstructionModeEnabled: "Construction Mode",
  doorProppedEventSettings: "Door Propped Events",
  doorClosedEventSettings: "Door Closed Events",
  doorOpenedEventSettings: "Door Opened Events",
  proppedDoorAlertThreshold: "Propped Door Alert Threshold",
  extendedAutoRelockDelay: "Extended Autolock Delay",
  autoRelockDelay: "Autolock Delay",
  beeperSetting: "Beeper",
  bleMobileCredentialSetting: "BLE Mobile Credentials",
  bleCredentialPerformance: "BLE Performance",
  bleCredentialReadingRange: "BLE Reading Range",
  antiPassbackDuration: "Anti-Passback Duration",
  iClassCredentials: "iClass Credential Mode",
  iso14443SecurityMethod: "ISO 14443 Security",
  iso15693SecurityMethod: "ISO 15693 Security",
  wifiConnectivitySetting: "Wi-Fi Enabled Status",
  timeZoneOffset: "Timezone Offset",
  dstSetting: "Daylight Savings Time",
  lockMode: "Lock Mode",
  noTour: "No Tour",
  noTourState: "No Tour State",
  noTourDoorId: "No Tour Door ID",
  noTourGroupIds: "No Tour Group IDs",
  noTourEncryptedSiteKey: "No Tour Site Key",
};

const PASSWORD_PROTECTED_SETTINGS = ["wifiConnectivitySetting"];

// These three fields are always submitted together in a single NoTourV1ConfigNoTour mutation
const NOTOUR_CONFIG_SETTINGS = ["noTourDoorId", "noTourGroupIds", "noTourEncryptedSiteKey"];

const SETTING_CONFIGS = {
  // 3-value radio
  interiorLockBlinkingEnabled: {
    inputType: "radio",
    options: [
      { value: "NORMALBLINKENABLED", label: "Normal Blink" },
      { value: "RAPIDBLINKENABLED", label: "Rapid Blink" },
      { value: "DISABLED", label: "Disabled" },
    ],
  },
  // Power fail — 3-option radio
  powerFailLockMode: {
    inputType: "radio",
    options: [
      { value: "AS_IS", label: "As Is (Keep Current State)" },
      { value: "FAILSECURE", label: "Fail Secure (Lock)" },
      { value: "FAILPASSAGE", label: "Fail Passage (Unlock)" },
    ],
  },
  // Credential security enums
  iClassCredentials: {
    inputType: "radio",
    options: [
      { value: "UID_FORTY_BIT_WITH_PARITY", label: "UID 40-bit with Parity" },
      { value: "UID_SIXTYFOUR_BIT_MSB", label: "UID 64-bit MSB" },
      { value: "SYMMETRIC_CRYPTOGRAPHIC_SECURITY", label: "Symmetric Cryptographic" },
      { value: "DISABLED", label: "Disabled" },
    ],
  },
  iso14443SecurityMethod: {
    inputType: "radio",
    options: [
      { value: "UID_ONLY", label: "UID Only" },
      { value: "SYMMETRIC_CRYPTOGRAPHIC_SECURITY", label: "Symmetric Cryptographic" },
      { value: "DISABLED", label: "Disabled" },
    ],
  },
  iso15693SecurityMethod: {
    inputType: "radio",
    options: [
      { value: "UID", label: "UID" },
      { value: "DISABLED", label: "Disabled" },
    ],
  },
  // BLE credential enums (UNSUPPORTED excluded)
  bleCredentialPerformance: {
    inputType: "radio",
    options: [
      { value: "MAX", label: "Max" },
      { value: "NORMAL", label: "Normal" },
    ],
  },
  bleCredentialReadingRange: {
    inputType: "radio",
    options: [
      { value: "LONG", label: "Long" },
      { value: "SHORT", label: "Short" },
    ],
  },
  // Sliders
  proppedDoorAlertThreshold: {
    inputType: "slider",
    min: 5,
    max: 30,
    unit: "seconds",
  },
  extendedAutoRelockDelay: {
    inputType: "slider",
    min: 30,
    max: 120,
    unit: "seconds",
  },
  autoRelockDelay: {
    inputType: "slider",
    min: 3,
    max: 30,
    unit: "seconds",
  },
  antiPassbackDuration: {
    inputType: "slider",
    min: 0,
    max: 60,
    unit: "seconds",
  },
  wifiConnectivitySetting: {
    inputType: "radio",
    options: [
      { value: "ENABLED", label: "Enabled" },
      { value: "DISABLED", label: "Disabled" },
    ],
    dangerWarning: "Disabling Wi-Fi will make this lock completely unreachable over the network. It can only be re-enabled via Bluetooth (BLE) by a technician on-site. The lock will become unmanageable remotely.",
  },
  timeZoneOffset: {
    inputType: "slider",
    min: -12,
    max: 14,
    unit: "UTC",
  },
  dstSetting: {
    inputType: "radio",
    options: [
      { value: "ENABLED", label: "Enabled" },
      { value: "DISABLED", label: "Disabled" },
    ],
  },
  lockMode: {
    inputType: "radio",
    options: [
      { value: "OFFICE", label: "Office" },
      { value: "PRIVACY", label: "Privacy" },
      { value: "STOREROOM", label: "Storeroom" },
      { value: "APARTMENT", label: "Apartment" },
    ],
  },
  noTour: {
    inputType: "radio",
    options: [
      { value: "ENABLED", label: "Enabled" },
      { value: "DISABLED", label: "Disabled" },
    ],
  },
  noTourState: {
    inputType: "radio",
    options: [
      { value: "ENABLED", label: "Enabled" },
      { value: "DISABLED", label: "Disabled" },
    ],
  },
  noTourDoorId: {
    inputType: "number",
    hint: "All three No Tour config fields are saved together in one mutation.",
  },
  noTourGroupIds: {
    inputType: "text",
    hint: "Comma-separated group IDs (e.g. 1,2,3). Saved together with Door ID and Site Key.",
  },
  noTourEncryptedSiteKey: {
    inputType: "textarea",
    hint: "Encrypted ENGAGE siteKey. Saved together with Door ID and Group IDs.",
  },
};

export function useCredentialsSettingsEditor({
  selectedDeviceId,
  updateCredentialsSetting,
  invalidateDeviceStateCache,
  fetchDeviceState,
  showNotification,
  onPasswordProtectedSave,
  getDeviceState,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [editingSettingKey, setEditingSettingKey] = useState(null);
  const [editingSettingLabel, setEditingSettingLabel] = useState(null);
  const [editingCurrentValue, setEditingCurrentValue] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Use ref to avoid stale closure issues in async operations
  const selectedDeviceIdRef = useRef(selectedDeviceId);
  selectedDeviceIdRef.current = selectedDeviceId;

  const editingSettingConfig = SETTING_CONFIGS[editingSettingKey] || null;

  const startEditing = useCallback((key, currentValue) => {
    setEditingSettingKey(key);
    setEditingSettingLabel(CREDENTIAL_SETTING_LABELS[key] || key);
    setEditingCurrentValue(currentValue);
    setIsOpen(true);
  }, []);

  const cancelEditing = useCallback(() => {
    setIsOpen(false);
    setEditingSettingKey(null);
    setEditingSettingLabel(null);
    setEditingCurrentValue(null);
    setIsSaving(false);
  }, []);

  const saveCredentialsSetting = useCallback(
    async (newValue) => {
      const currentDeviceId = selectedDeviceIdRef.current;

      // Password-protected settings go through a separate flow
      if (PASSWORD_PROTECTED_SETTINGS.includes(editingSettingKey) && onPasswordProtectedSave) {
        cancelEditing();
        onPasswordProtectedSave(
          editingSettingKey,
          newValue,
          currentDeviceId,
          () => {
            invalidateDeviceStateCache(currentDeviceId);
            fetchDeviceState(currentDeviceId);
          }
        );
        return;
      }

      setIsSaving(true);
      try {
        const isNoTourConfig = NOTOUR_CONFIG_SETTINGS.includes(editingSettingKey);
        let payload = { deviceId: currentDeviceId, setting: editingSettingKey, value: newValue };
        if (isNoTourConfig) {
          const currentState = getDeviceState ? getDeviceState(currentDeviceId) : {};
          const merged = {
            noTourDoorId: currentState?.noTourDoorId,
            noTourGroupIds: currentState?.noTourGroupIds,
            noTourEncryptedSiteKey: currentState?.noTourEncryptedSiteKey,
            [editingSettingKey]: newValue,
          };
          payload = { deviceId: currentDeviceId, setting: editingSettingKey, noTourConfig: merged };
        }
        await updateCredentialsSetting(payload);

        invalidateDeviceStateCache(currentDeviceId);

        showNotification({
          message: `${CREDENTIAL_SETTING_LABELS[editingSettingKey] || editingSettingKey} change submitted to device.`,
          type: "info",
        });

        cancelEditing();
        fetchDeviceState(currentDeviceId);
      } catch (error) {
        logger.error("Failed to update credential setting:", error);
        showNotification({
          message: "Failed to update setting.",
          type: "error",
        });
      } finally {
        setIsSaving(false);
      }
    },
    [
      editingSettingKey,
      updateCredentialsSetting,
      invalidateDeviceStateCache,
      fetchDeviceState,
      showNotification,
      cancelEditing,
      onPasswordProtectedSave,
    ]
  );

  return {
    isOpen,
    editingSettingKey,
    editingSettingLabel,
    editingCurrentValue,
    editingSettingConfig,
    isSaving,
    startEditing,
    cancelEditing,
    saveCredentialsSetting,
  };
}
